import * as React from 'react'
import { useMutation } from '@tanstack/react-query'

import { RolePermissionModel } from 'app/models/rolePermissionModel'
import { validateEmail } from 'app/common/validationHelper'
import { useQueryCompanyRoles } from '@/lib/api/company/roles'
import { useQueryDriverGroups } from '@/lib/api/company/drivers/useQueryDriverGroups'
import { useQueryVehicleGroups } from '@/lib/api/company/vehicle-list/useQueryVehicleGroups'
import { axiosInstance } from '@/modules/shared/lib/axios'
import { Dropdown } from '@/ui/form/controls/Dropdown'
import { TextAreaField } from '@/ui/form/controls/TextAreaField'
import { AdminModal } from '../../shared/AdminModal'

type Role = { id: number; name: string }

type Row = {
  email: string
  firstName: string
  surname: string
  /** null = brak kolumny ról, idą role domyślne. */
  roleNames: string[] | null
  /** null = wszyscy kierowcy / wszystkie pojazdy. */
  driverGroupNames: string[] | null
  vehicleGroupNames: string[] | null
}

type Group = { id: number; name?: string }

const ALL = 'wszyscy'

const splitNames = (cell: string | undefined) => {
  const names = (cell ?? '').split(',').map((name) => name.trim()).filter(Boolean)
  return names.length === 0 ? null : names
}

/** Pusta komórka albo „Wszyscy” = bez ograniczeń. Nazwa pasuje do każdej grupy o tej nazwie (bez wielkości liter). */
const resolveGroups = (names: string[] | null, groups: Group[]) => {
  if (names == null || names.some((name) => name.toLowerCase() === ALL)) {
    return { ids: null as number[] | null, unknown: [] as string[] }
  }
  const unknown: string[] = []
  const ids = names.flatMap((name) => {
    const found = groups.filter((group) => group.name?.trim().toLowerCase() === name.toLowerCase())
    if (found.length === 0) unknown.push(name)
    return found.map((group) => group.id)
  })
  return { ids: Array.from(new Set(ids)), unknown }
}

type BulkInviteResult = {
  index: number
  email: string
  /** `saved` dostaje administrator firmy - bez rozróżniania nowej i istniejącej osoby. */
  status: 'invited' | 'updated' | 'saved' | 'error'
  code: string | null
}

const STATUS_LABEL: Record<BulkInviteResult['status'], string> = {
  invited: 'Zaproszony',
  updated: 'Istniejące konto - dopisano',
  saved: 'Zapisano',
  error: 'Błąd',
}

/** Wiersze z Excela (tab) albo `;`. Pierwszy wiersz bez `@` to nagłówek. */
const parseRows = (text: string): Row[] =>
  text
    .split(/\r?\n/)
    .map((line) => line.trim())
    .filter((line) => line.length > 0)
    .map((line) => line.split(line.includes('\t') ? '\t' : ';').map((cell) => cell.trim()))
    .filter((cells, index) => index > 0 || cells[0]?.includes('@'))
    .map(([email = '', firstName = '', surname = '', roles, driverGroups, vehicleGroups]) => ({
      email,
      firstName,
      surname,
      roleNames: splitNames(roles),
      driverGroupNames: splitNames(driverGroups),
      vehicleGroupNames: splitNames(vehicleGroups),
    }))

type Props = {
  companyId: string
  roles: RolePermissionModel[]
  onClose: () => void
  onDone: () => void
}

/**
 * Masowe zaproszenia do firmy: e-mail, imię, nazwisko i opcjonalnie role po nazwie.
 * Nowe osoby dostają konto w Keycloaku i mail z zaproszeniem, istniejącym role są dopisywane.
 */
export const BulkInviteUsersModal: React.FC<Props> = ({
  companyId,
  roles,
  onClose,
  onDone,
}) => {
  const [text, setText] = React.useState('')
  const [defaultRoleIds, setDefaultRoleIds] = React.useState<string[]>([])
  const [results, setResults] = React.useState<BulkInviteResult[] | null>(null)

  const companyRoles = useQueryCompanyRoles(companyId)
  const available = React.useMemo<Role[]>(() => {
    const own = (companyRoles.data?.roles ?? []).filter(
      (role) => !roles.some((item) => String(item.id) === String(role.id)),
    )
    return [...roles, ...own]
      .filter((role) => !(role as { isDriver?: boolean }).isDriver)
      .map((role) => ({ id: Number(role.id), name: role.name }))
  }, [roles, companyRoles.data])

  const driverGroups = useQueryDriverGroups({ companyId })
  const vehicleGroups = useQueryVehicleGroups({ companyId })

  const rows = React.useMemo(() => parseRows(text), [text])

  const resolved = React.useMemo(() => {
    const seen = new Set<string>()
    return rows.map((row) => {
      const key = row.email.toLowerCase()
      const duplicate = seen.has(key)
      seen.add(key)

      const unknownRoles: string[] = []
      const roleIds = row.roleNames
        ? row.roleNames.flatMap((name) => {
            const role = available.find(
              (item) => item.name.trim().toLowerCase() === name.toLowerCase(),
            )
            if (!role) unknownRoles.push(name)
            return role ? [role.id] : []
          })
        : defaultRoleIds.map(Number)

      const drivers = resolveGroups(row.driverGroupNames, driverGroups.data ?? [])
      const vehicles = resolveGroups(row.vehicleGroupNames, vehicleGroups.data ?? [])

      const error = !validateEmail(row.email)
        ? 'Niepoprawny e-mail'
        : duplicate
          ? 'Powtórzony e-mail'
          : unknownRoles.length > 0
            ? `Nieznana rola: ${unknownRoles.join(', ')}`
            : drivers.unknown.length > 0
              ? `Nieznana grupa kierowców: ${drivers.unknown.join(', ')}`
              : vehicles.unknown.length > 0
                ? `Nieznana grupa pojazdów: ${vehicles.unknown.join(', ')}`
                : null

      return {
        ...row,
        roleIds,
        driverGroupIds: drivers.ids,
        vehicleGroupIds: vehicles.ids,
        driverGroupLabel: drivers.ids == null ? 'Wszyscy' : row.driverGroupNames!.join(', '),
        vehicleGroupLabel: vehicles.ids == null ? 'Wszystkie' : row.vehicleGroupNames!.join(', '),
        error,
        roleLabels: roleIds.map((id) => available.find((role) => role.id === id)?.name ?? id),
      }
    })
  }, [rows, available, defaultRoleIds, driverGroups.data, vehicleGroups.data])

  const invalid = resolved.filter((row) => row.error).length

  const invite = useMutation<BulkInviteResult[], unknown, void>({
    mutationFn: async () => {
      const res = await axiosInstance.post('/config-api/user/bulkInvite', {
        companyId,
        entries: resolved.map((row) => ({
          email: row.email,
          firstName: row.firstName,
          surname: row.surname,
          roleIds: row.roleIds,
          driverGroupIds: row.driverGroupIds,
          vehicleGroupIds: row.vehicleGroupIds,
        })),
      })
      return res.data
    },
    onSuccess: (data) => {
      setResults(data)
      onDone()
    },
  })

  const resultFor = (index: number) => results?.find((item) => item.index === index)

  return (
    <AdminModal
      title="Zaproś wielu użytkowników"
      icon={null}
      width={1000}
      onClose={onClose}
      onConfirm={results ? undefined : () => invite.mutate()}
      confirmDisabled={
        resolved.length === 0 ||
        invalid > 0 ||
        invite.isPending ||
        driverGroups.isLoading ||
        vehicleGroups.isLoading
      }
      confirmLabel={invite.isPending ? 'Zapraszanie…' : `Zaproś (${resolved.length})`}
    >
      {!results && (
        <div className="tw-mb-4 tw-flex tw-flex-col tw-gap-3">
          <TextAreaField
            label="Wklej z Excela: e-mail, imię, nazwisko, role, grupy kierowców, grupy pojazdów"
            helper="Kolumny rozdzielone tabulatorem albo średnikiem, kilka ról lub grup po przecinku. Puste grupy albo „Wszyscy” = bez ograniczeń. Nowe osoby dostaną e-mail z zaproszeniem, istniejącym role zostaną dopisane."
            rows={8}
            fullWidth
            value={text}
            onChange={(event) => setText(event.target.value)}
          />
          <div className="tw-flex tw-items-center tw-gap-4">
            <span className="tw-w-60 tw-flex-shrink-0 tw-text-sm">
              Role dla wierszy bez kolumny ról
            </span>
            <Dropdown
              multiple
              showSelectedNames
              portalMenu
              placeholder="Bez ról"
              options={available.map((role) => ({
                value: String(role.id),
                label: role.name,
              }))}
              value={defaultRoleIds}
              onChange={setDefaultRoleIds}
            />
          </div>
        </div>
      )}

      {invite.isError && (
        <p className="tw-mb-2 tw-text-sm tw-text-red-600">
          Nie udało się wysłać zaproszeń.
        </p>
      )}

      {resolved.length > 0 && (
        <table className="tw-w-full tw-text-sm">
          <thead>
            <tr className="tw-border-b tw-border-base-300 tw-text-left tw-text-xs tw-uppercase">
              <th className="tw-py-1">E-mail</th>
              <th>Imię</th>
              <th>Nazwisko</th>
              <th>Role</th>
              <th>Kierowcy</th>
              <th>Pojazdy</th>
              <th>Status</th>
            </tr>
          </thead>
          <tbody>
            {resolved.map((row, index) => {
              const result = resultFor(index)
              const error = row.error ?? (result?.status === 'error' ? result.code : null)
              return (
                <tr key={index} className="tw-border-b tw-border-base-200">
                  <td className="tw-py-1">{row.email}</td>
                  <td>{row.firstName}</td>
                  <td>{row.surname}</td>
                  <td>{row.roleLabels.join(', ') || '-'}</td>
                  <td>{row.driverGroupLabel}</td>
                  <td>{row.vehicleGroupLabel}</td>
                  <td className={error ? 'tw-text-red-600' : 'tw-text-primary'}>
                    {error ?? (result ? STATUS_LABEL[result.status] : '')}
                  </td>
                </tr>
              )
            })}
          </tbody>
        </table>
      )}
    </AdminModal>
  )
}
